import {
  DrawingCanvas,
  DrawingCanvasMode,
  type DrawingCanvasShape,
  type DrawingCanvasShapeKey,
  type Point,
} from '@civilgrid/ui/components';
import { cn } from '@civilgrid/ui/utils';
import { useEffect, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import type { MapDetail, MapObject } from '~/common/types';
import { useMapObjectMutations } from '~/hooks/civilDrawV2/useMapObjectMutations';
import { isLineOrPolygon, toCanvasShapes, toNewMapObject, toUpdatedMapObject } from './civildrawCanvasAdapter';
import type { DrawingTool } from './DrawToolButtons';

type CivilDrawDrawingCanvasProps = {
  projectId: string;
  mapId: string;
  mapObjects: MapDetail['objects'];
  canEdit: boolean;
  canDelete: boolean;
  tool: DrawingTool;
  onToolChange: (tool: DrawingTool) => void;
};

type Size = { width: number; height: number };

/** Draws a CivilDraw map's lines and polygons on DrawingCanvas over the map, and saves edits to them. */
export default function CivilDrawDrawingCanvas({
  projectId,
  mapId,
  mapObjects: loadedMapObjects,
  canEdit,
  canDelete,
  tool,
  onToolChange,
}: CivilDrawDrawingCanvasProps) {
  const map = useMap().current?.getMap();
  const { createMapObject, updateMapObject, deleteMapObject } = useMapObjectMutations(projectId, mapId);
  const [wrapper, setWrapper] = useState<HTMLDivElement | null>(null);
  const [size, setSize] = useState<Size>();
  const [, setMapView] = useState<string>();
  const [lastEdit, setLastEdit] = useState<{ before: MapDetail['objects']; mapObject: MapObject }>();
  const isDrawing = tool.mode === DrawingCanvasMode.DRAW;

  // Shows the last edit until new map objects arrive, since TanStack Query passes on cache changes a tick late
  const mapObjects =
    lastEdit?.before === loadedMapObjects
      ? { ...loadedMapObjects, [lastEdit.mapObject.id]: lastEdit.mapObject }
      : loadedMapObjects;

  useEffect(() => {
    if (!map) return;
    const resize = () => {
      const container = map.getContainer();
      setSize({ width: container.clientWidth, height: container.clientHeight });
    };
    const updateMapView = () => {
      const { lng, lat } = map.getCenter();
      // Updating the state when the map moves triggers a rerender.
      // We do this so that the shapes are redrawn in the right place as the map moves.
      setMapView(`${lng},${lat},${map.getZoom()},${map.getBearing()},${map.getPitch()}`);
    };
    resize();
    map.on('move', updateMapView);
    map.on('resize', resize);
    return () => {
      map.off('move', updateMapView);
      map.off('resize', resize);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !wrapper) return;
    // Note: While drawing a shape does not yet exist as long/lat, so we block zooming and moving the map.
    // TODO(CG-1771): update passWheelToMap and passKeyToMap to allow zoom/pan/move while drawing.
    const passWheelToMap = (event: WheelEvent) => {
      event.preventDefault();
      if (!isDrawing) map.getCanvas().dispatchEvent(new WheelEvent('wheel', event));
    };
    const passKeyToMap = (event: KeyboardEvent) => {
      // Only pass along events that are targeting elements in this component's wrapper.
      if (isDrawing || event.defaultPrevented || !wrapper.contains(event.target as Node)) return;
      map.getCanvas().dispatchEvent(new KeyboardEvent('keydown', event));
    };
    // We use {passive: false} so we can call preventDefault, so a trackpad pinch zooms the map instead of the page.
    wrapper.addEventListener('wheel', passWheelToMap, { passive: false });
    document.addEventListener('keydown', passKeyToMap);
    return () => {
      wrapper.removeEventListener('wheel', passWheelToMap);
      document.removeEventListener('keydown', passKeyToMap);
    };
  }, [map, wrapper, isDrawing]);

  if (!map || !size) return null;

  const toCanvasCoords = (position: GeoJSON.Position): Point => {
    const { x, y } = map.project([position[0], position[1]]);
    return { x, y };
  };
  const toLngLat = (point: Point): GeoJSON.Position => map.unproject([point.x, point.y]).toArray();

  const passMousedownToMap = (event: React.PointerEvent) => {
    map.getCanvas().dispatchEvent(new MouseEvent('mousedown', event.nativeEvent));
  };

  const handleShapeCreate = (canvasShape: DrawingCanvasShape) => {
    const mapObject = toNewMapObject(canvasShape, mapObjects, toLngLat);
    if (mapObject) createMapObject(mapObject);
  };

  const handleShapeChange = (canvasShape: DrawingCanvasShape) => {
    const mapObject = mapObjects[canvasShape.key];
    if (!mapObject || !isLineOrPolygon(mapObject)) return;
    const updatedMapObject = toUpdatedMapObject(mapObject, canvasShape, toLngLat);
    setLastEdit({ before: loadedMapObjects, mapObject: updatedMapObject });
    updateMapObject(updatedMapObject);
  };

  const handleShapeDelete = (key: DrawingCanvasShapeKey) => {
    if (canDelete) deleteMapObject(key);
  };

  return (
    <div ref={setWrapper}>
      <DrawingCanvas
        width={size.width}
        height={size.height}
        drawingShapes={toCanvasShapes(mapObjects, toCanvasCoords)}
        mode={tool.mode}
        drawingModeShapeType={tool.shapeType}
        onModeChange={(mode) => onToolChange({ mode })}
        onShapeCreate={handleShapeCreate}
        onShapeChange={handleShapeChange}
        onShapeDelete={handleShapeDelete}
        onBackgroundPointerDown={passMousedownToMap}
        onContextMenu={(event) => event.preventDefault()}
        aria-label="CivilDraw lines and polygons"
        data-testid="civildraw-drawing-canvas"
        className={cn('absolute left-0 top-0', canEdit ? 'pointer-events-auto' : 'pointer-events-none')}
      />
    </div>
  );
}
